import { Flex, SkeletonBox, Spacing } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'

import { BankTransactionsContent } from '../BankTransactions/BankTransactionsContent'
import { SandboxModeProvider } from '../BankTransactions/contexts'
import { BankTransactionsSandboxPane } from './elements/BankTransactionsSandboxPane'

const SANDBOX_LOADING_DURATION_MS = 800

interface BankTransactionsSandboxProps {
  onClose: () => void
}

const SandboxLoadingState = (): ReactElement => (
  <Flex flexDirection="column" gap={Spacing.S16}>
    <SkeletonBox fullWidth height={64} />
    <SkeletonBox fullWidth height={64} />
    <SkeletonBox fullWidth height={64} />
    <SkeletonBox fullWidth height={64} />
  </Flex>
)

/**
 * Entry point for the customer-facing bank transactions sandbox: a full-screen pane,
 * opened from the Bank Accounts page, that always renders BankTransactionsContent with
 * the sandbox data adapter — no real backend calls, no toggle, no persistence. Shows a
 * brief loading state first so opening the demo doesn't feel like an instant content swap.
 */
export const BankTransactionsSandbox = ({ onClose }: BankTransactionsSandboxProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const [resetKey, setResetKey] = useState(0)

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), SANDBOX_LOADING_DURATION_MS)

    return () => clearTimeout(timer)
  }, [])

  // Reset is a fresh mount, not a new action: keying SandboxModeProvider forces it (and
  // everything nested inside BankTransactionsContent) to remount, re-running every lazy
  // initializer and useState default in one step — seed data, drawer selection, filters, all
  // of it — rather than trying to reach each piece of state individually.
  const handleReset = useCallback(() => setResetKey((key) => key + 1), [])

  return (
    <BankTransactionsSandboxPane onClose={onClose} onReset={handleReset}>
      {(cardElement) =>
        isLoading ? (
          <SandboxLoadingState />
        ) : (
          <SandboxModeProvider key={resetKey}>
            <BankTransactionsContent drawerPortalContainer={cardElement ?? undefined} />
          </SandboxModeProvider>
        )
      }
    </BankTransactionsSandboxPane>
  )
}
